/**
 * DuplicatesPage — Partner duplikacio felismeres & 60 napos elhagyott gep
 * Epic 72: CRM Duplikáció & 60 Napos Gep
 *
 * Duplikacio lista, merge/dismiss muveletek, elhagyott gep protokoll.
 */

import type { AbandonedAction } from '@/api/partner-dedup';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useAbandonedEquipment,
  useDismissDuplicate,
  useDuplicateMatches,
  useMergePartners,
  useRecordAbandonedProtocol,
} from '@/hooks/usePartnerDedup';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';

// ACTION_LABELS moved inside component for i18n

export function DuplicatesPage() {
  const { t } = useTranslation();

  const ACTION_LABELS: Record<string, string> = {
    DESTROY: t('partner.actionLabels.DESTROY'),
    SELL: t('partner.actionLabels.SELL'),
    KEEP: t('partner.actionLabels.KEEP'),
  };

  const MATCH_FIELD_LABELS: Record<string, string> = {
    PHONE: t('partner.phone'),
    DOCUMENT_NUMBER: t('partner.matchFieldLabels.DOCUMENT_NUMBER'),
    EMAIL: t('partner.matchFieldLabels.EMAIL'),
    NAME: t('partner.matchFieldLabels.NAME'),
    TAX_NUMBER: t('partner.matchFieldLabels.TAX_NUMBER'),
  };
  const {
    matches,
    isLoading: matchesLoading,
    error: matchesError,
    refetch: refetchMatches,
  } = useDuplicateMatches();
  const {
    logs,
    isLoading: logsLoading,
    error: logsError,
    refetch: refetchLogs,
  } = useAbandonedEquipment();
  const mergeMutation = useMergePartners();
  const dismissMutation = useDismissDuplicate();
  const protocolMutation = useRecordAbandonedProtocol();

  // FG-2-2: Side-by-side compare state
  const [compareMatch, setCompareMatch] = useState<{
    matchId: string;
    sourceId: string;
    targetId: string;
    matchField: string;
    confidence: number;
  } | null>(null);

  const handleMerge = async (matchId: string, sourceId: string, matchPartnerId: string) => {
    // FG-2-2: Nyissuk a side-by-side összehasonlítást merge helyett
    const match = matches.find(m => m.id === matchId);
    setCompareMatch({
      matchId,
      sourceId,
      targetId: matchPartnerId,
      matchField: match?.matchField ?? '',
      confidence: match?.confidence ?? 0,
    });
  };

  const handleConfirmMerge = async (primaryId: string, secondaryId: string) => {
    if (!compareMatch) return;
    await mergeMutation.mutateAsync({
      matchId: compareMatch.matchId,
      input: { primaryId, secondaryId },
    });
    setCompareMatch(null);
    refetchMatches();
  };

  const handleDismiss = async (matchId: string) => {
    await dismissMutation.mutateAsync(matchId);
    refetchMatches();
  };

  const handleProtocol = async (logId: string, action: AbandonedAction) => {
    const note = action === 'DESTROY' ? prompt(t('partner.scrappingReason')) : undefined;
    await protocolMutation.mutateAsync({
      logId,
      input: {
        action,
        note: note !== null ? note : undefined,
      },
    });
    refetchLogs();
  };

  const isLoading = matchesLoading || logsLoading;
  const hasError = matchesError || logsError;

  if (isLoading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">{t('partner.duplicatesTitle')}</h1>
        <div className="text-center py-8 text-gray-500 dark:text-[#ccfbf1]">
          {t('common.loadingText')}
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">{t('partner.duplicatesTitle')}</h1>
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-red-500">
              {t('common.errorLabel')}: {matchesError ?? logsError}
            </p>
            <Button
              className="mt-4"
              onClick={() => {
                refetchMatches();
                refetchLogs();
              }}
            >
              {t('common.retryButton')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">{t('partner.duplicatesTitle')}</h1>

      {/* Duplicate Matches */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>
            {t('partner.duplicateSuspects')} ({matches.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {matches.length === 0 ? (
            <p className="text-gray-500 dark:text-[#ccfbf1] text-center py-4">
              {t('partner.noDuplicates')}
            </p>
          ) : (
            <div className="divide-y dark:divide-[#2d7d8a]">
              {matches.map(match => (
                <div key={match.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="font-medium">
                        {match.sourcePartnerId.slice(0, 8)} &harr;{' '}
                        {match.matchPartnerId.slice(0, 8)}
                      </span>
                      <span className="px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded text-xs">
                        {MATCH_FIELD_LABELS[match.matchField] ?? match.matchField}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-[#ccfbf1]">
                        {match.confidence}% {t('partner.confidence')}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 dark:text-[#99f6e4] mt-1">
                      {t('partner.detected')}:{' '}
                      {new Date(match.createdAt).toLocaleDateString('hu-HU')}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() =>
                        handleMerge(match.id, match.sourcePartnerId, match.matchPartnerId)
                      }
                      disabled={mergeMutation.isPending}
                    >
                      {t('partner.mergeButton')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDismiss(match.id)}
                      disabled={dismissMutation.isPending}
                    >
                      {t('partner.dismissButton')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Abandoned Equipment */}
      <Card>
        <CardHeader>
          <CardTitle>
            {t('partner.abandonedEquipment')} ({logs.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-gray-500 dark:text-[#ccfbf1] text-center py-4">
              {t('partner.noAbandonedEquipment')}
            </p>
          ) : (
            <div className="divide-y dark:divide-[#2d7d8a]">
              {logs.map(log => (
                <div key={log.id} className="py-3 flex items-center justify-between">
                  <div>
                    <span className="font-medium">
                      {t('partner.worksheetLabel')}: {log.worksheetId.slice(0, 12)}
                    </span>
                    <span className="ml-3 text-sm text-gray-500 dark:text-[#ccfbf1]">
                      {t('partner.detected')}:{' '}
                      {new Date(log.detectedAt).toLocaleDateString('hu-HU')}
                    </span>
                    {log.action && (
                      <span className="ml-3 text-sm font-medium text-blue-600">
                        {ACTION_LABELS[log.action] ?? log.action}
                      </span>
                    )}
                    {log.note && (
                      <p className="text-xs text-gray-400 dark:text-[#99f6e4] mt-1">{log.note}</p>
                    )}
                  </div>
                  {!log.action && (
                    <div className="flex gap-2">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleProtocol(log.id, 'DESTROY')}
                        disabled={protocolMutation.isPending}
                      >
                        {t('partner.scrapButton')}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleProtocol(log.id, 'SELL')}
                        disabled={protocolMutation.isPending}
                      >
                        {t('partner.sellButton')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleProtocol(log.id, 'KEEP')}
                        disabled={protocolMutation.isPending}
                      >
                        {t('partner.keepButton')}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* FG-2-2: Side-by-side összehasonlító overlay */}
      {compareMatch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{t('partner.compareTitle')}</CardTitle>
              <Button variant="outline" size="sm" onClick={() => setCompareMatch(null)}>
                ✕
              </Button>
            </CardHeader>
            <CardContent>
              {/* Match info */}
              <div className="mb-4 flex items-center gap-3 text-sm">
                <span className="px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded text-xs">
                  {MATCH_FIELD_LABELS[compareMatch.matchField] ?? compareMatch.matchField}
                </span>
                <span className="text-muted-foreground">
                  {compareMatch.confidence}% {t('partner.confidence')}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {/* Partner A (source) */}
                <div className="space-y-2 rounded-lg border p-4 dark:border-[#2d7d8a]">
                  <h3 className="font-semibold text-lg">{t('partner.partnerA')}</h3>
                  <p className="text-xs text-muted-foreground break-all">
                    ID: {compareMatch.sourceId.slice(0, 8)}…
                  </p>
                  <a
                    href={`/partners/${compareMatch.sourceId}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-primary hover:underline"
                  >
                    {t('partner.openInNewTab')}
                  </a>
                </div>
                {/* Partner B (match) */}
                <div className="space-y-2 rounded-lg border p-4 dark:border-[#2d7d8a]">
                  <h3 className="font-semibold text-lg">{t('partner.partnerB')}</h3>
                  <p className="text-xs text-muted-foreground break-all">
                    ID: {compareMatch.targetId.slice(0, 8)}…
                  </p>
                  <a
                    href={`/partners/${compareMatch.targetId}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-primary hover:underline"
                  >
                    {t('partner.openInNewTab')}
                  </a>
                </div>
              </div>

              <p className="my-4 text-sm text-muted-foreground">
                {t('partner.compareDescription')}
              </p>

              {/* Merge action buttons */}
              <div className="mt-6 flex gap-3 justify-end border-t pt-4 dark:border-[#2d7d8a]">
                <Button variant="outline" onClick={() => setCompareMatch(null)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={() => handleConfirmMerge(compareMatch.sourceId, compareMatch.targetId)}
                  disabled={mergeMutation.isPending}
                >
                  {t('partner.mergeIntoA')}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleConfirmMerge(compareMatch.targetId, compareMatch.sourceId)}
                  disabled={mergeMutation.isPending}
                >
                  {t('partner.mergeIntoB')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
